import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button, useTheme } from "@mui/material";
import { useContextProvider } from "../page-context/ContextProvider";

/**
 * Triangle warning icon
 */
const TriangleWarning = ({ className, ...props }) => (
  <svg
    className={className}
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: "18px", height: "18px", shrink: 0 }}
    {...props}
  >
    <g fill="currentColor">
      <path
        d="M7.63796 3.48996L2.21295 12.89C1.60795 13.9399 2.36395 15.25 3.57495 15.25H14.425C15.636 15.25 16.392 13.9399 15.787 12.89L10.362 3.48996C9.75696 2.44996 8.24296 2.44996 7.63796 3.48996Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M9 6.75V9.75"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M9 13.5C8.448 13.5 8 13.05 8 12.5C8 11.95 8.448 11.5 9 11.5C9.552 11.5 10 11.9501 10 12.5C10 13.0499 9.552 13.5 9 13.5Z"
        fill="currentColor"
        stroke="none"
      />
    </g>
  </svg>
);

/**
 * Gradient spinner loader
 */
const Loader2 = ({ className, ...props }) => (
  <svg
    className={className}
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: "18px", height: "18px", shrink: 0 }}
    {...props}
  >
    <g fill="currentColor">
      <path
        d="M9 16.25C13.0041 16.25 16.25 13.0041 16.25 9C16.25 4.99594 13.0041 1.75 9 1.75"
        fill="none"
        stroke="url(#nc-loader-2-outline-gradient-1)"
        strokeWidth="1.5"
      />
      <path
        d="M9 16.25C4.99594 16.25 1.75 13.0041 1.75 9C1.75 4.99594 4.99594 1.75 9 1.75"
        fill="none"
        stroke="url(#nc-loader-2-outline-gradient-2)"
        strokeWidth="1.5"
      />
      <circle cx="9" cy="16.25" fill="currentColor" r="0.75" stroke="none" />
      <defs>
        <linearGradient
          id="nc-loader-2-outline-gradient-1"
          gradientUnits="userSpaceOnUse"
          x1="9"
          x2="9"
          y1="2.5"
          y2="16.25"
        >
          <stop stopColor="currentColor" stopOpacity="0.5" />
          <stop offset="1" stopColor="currentColor" />
        </linearGradient>
        <linearGradient
          id="nc-loader-2-outline-gradient-2"
          gradientUnits="userSpaceOnUse"
          x1="9"
          x2="9"
          y1="2.5"
          y2="16.25"
        >
          <stop stopColor="currentColor" stopOpacity="0.5" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
    </g>
  </svg>
);

/**
 * Circle check icon for success state
 */
const CircleCheck = ({ className, color, ...props }) => (
  <div
    className={className}
    style={{
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      backgroundColor: color || "#659d33",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      ...props.style
    }}
  >
    <svg
      viewBox="0 0 12 12"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "12px", height: "12px", color: "white" }}
    >
      <path
        d="M3.707 6.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L5 7.586 3.707 6.293z"
        fill="currentColor"
      />
    </svg>
  </div>
);

/**
 * Circle warning icon for error state
 */
const CircleWarning = ({ className, ...props }) => (
  <svg
    className={className}
    viewBox="0 0 12 12"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: "18px", height: "18px", shrink: 0 }}
    {...props}
  >
    <g fill="currentColor">
      <path
        d="m6,0C2.691,0,0,2.691,0,6s2.691,6,6,6,6-2.691,6-6S9.309,0,6,0Zm-.75,3.5c0-.414.336-.75.75-.75s.75.336.75.75v3c0,.414-.336.75-.75.75s-.75-.336-.75-.75v-3Zm.75,6.25c-.482,0-.875-.393-.875-.875s.393-.875.875-.875.875.393.875.875-.393.875-.875.875Z"
        fill="currentColor"
        strokeWidth="0"
      />
    </g>
  </svg>
);

/**
 * Close X icon
 */
const XIcon = ({ className, ...props }) => (
  <svg
    className={className}
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: "16px", height: "16px", shrink: 0 }}
    {...props}
  >
    <path
      d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const UnsavedChanges = ({
  open,
  isSaving: propIsSaving,
  success: propSuccess,
  error: propError,
  onSave,
  onReset,
  label: customLabel
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { 
    isLoading, 
    setIsLoading, 
    isSaveClicked, 
    setIsSaveClicked, 
    isSaveVisible, 
    setIsSaveVisible,
    isSaveSuccess,
    setIsSaveSuccess,
    isSaveError,
    setIsSaveError,
    setRefresh,
    leftSidebarCollapse,
    rightSidebarCollapse
  } = useContextProvider();

  // Color from palette
  const brandColor = theme.palette.arivuLogocolor1 || "#659d33";

  // Determine final states based on props vs context
  const finalOpen = open !== undefined ? open : isSaveVisible;
  const finalIsSaving = propIsSaving !== undefined ? propIsSaving : isLoading;
  const finalIsSuccess = propSuccess !== undefined ? propSuccess : isSaveSuccess;
  const finalIsError = propError !== undefined ? propError : isSaveError;

  // Labels
  const label = customLabel || t("Unsaved Changes");
  const savingLabel = t("Saving…");
  const successLabel = t("Changes saved");
  const errorLabel = t("An error occurred");
  const saveLabel = t("Save");

  // Auto-success detection
  const wasSaving = useRef(false);
  useEffect(() => {
    if (finalIsSaving && isSaveClicked) {
      wasSaving.current = true;
    } else if (wasSaving.current && !finalIsSaving && !isSaveClicked && !finalIsError) {
      // Transition from saving to not saving (success)
      setIsSaveSuccess(true);
      wasSaving.current = false;
      const timer = setTimeout(() => {
        setIsSaveSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [finalIsSaving, isSaveClicked, finalIsError, setIsSaveSuccess]);

  const handleSave = () => {
    console.log("[UnsavedChanges] 💾 SAVE CLICKED");
    if (onSave) {
      onSave();
    } else {
      setIsSaveSuccess(false);
      setIsSaveError(false);
      setIsLoading(true);
      setIsSaveClicked(true);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setIsSaveVisible(false);
    setIsSaveSuccess(false);
    setIsSaveError(false);
    setIsSaveClicked(false);
    setIsLoading(false);
  };

  // Calculate horizontal offset to center in content area (between sidebars)
  const leftSidebarWidth = leftSidebarCollapse ? 80 : 280;
  const rightSidebarWidth = 0;
  const horizontalOffset = (leftSidebarWidth - rightSidebarWidth) / 2;
  const darkBrandColor = "#4a7c21"; // Darker green for text legibility

  return createPortal(
    <AnimatePresence>
      {(finalOpen || finalIsSuccess || finalIsError) && (
        <motion.div
          initial={{ opacity: 0, y: 20, x: `calc(-50% + ${horizontalOffset}px)`, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, x: `calc(-50% + ${horizontalOffset}px)`, scale: 1 }}
          exit={{ opacity: 0, y: 20, x: `calc(-50% + ${horizontalOffset}px)`, scale: 0.95 }}
          transition={{
            type: "spring",
            duration: 0.4,
            bounce: 0.15,
          }}
          style={{
            position: "fixed",
            bottom: "66px", // Thinner gap above the footer (58px)
            left: "50%",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between", // Use space-between for stable layout
            gap: "16px",
            borderRadius: "999px",
            padding: "6px 8px 6px 20px",
            fontSize: "14px",
            fontWeight: 700,
            backgroundColor: "rgba(240, 253, 244, 0.85)", // Soft light green glass
            backdropFilter: "blur(12px) saturate(180%)",
            WebkitBackdropFilter: "blur(12px) saturate(180%)",
            border: `1px solid ${brandColor}40`,
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
            color: darkBrandColor,
            minHeight: "44px",
            minWidth: "300px", // STABLE WIDTH: Prevents the "struggle" during transitions
          }}
          role="alert"
          aria-live="polite"
        >
          {/* Content Container (Icons + Labels) */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
            <AnimatePresence mode="wait" initial={false}>
              {finalIsError ? (
                <motion.div
                  key="error-icon"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <CircleWarning style={{ color: theme.palette.error.main }} />
                </motion.div>
              ) : finalIsSuccess ? (
                <motion.div
                  key="success-icon"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <CircleCheck color={brandColor} />
                </motion.div>
              ) : finalIsSaving ? (
                <motion.div
                  key="saving-icon"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Loader2 style={{ color: brandColor, animation: "spin 1s linear infinite" }} />
                </motion.div>
              ) : (
                <motion.div
                  key="default-icon"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <TriangleWarning style={{ color: brandColor }} />
                </motion.div>
              )}
            </AnimatePresence>
            
            <AnimatePresence mode="wait" initial={false}>
              <motion.span 
                key={finalIsError ? "err" : finalIsSuccess ? "suc" : finalIsSaving ? "sav" : "def"}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 5 }}
                transition={{ duration: 0.2 }}
                style={{ 
                  whiteSpace: "nowrap", 
                  display: "flex",
                  alignItems: "center",
                  color: darkBrandColor
                }}
              >
                {finalIsError
                  ? errorLabel
                  : finalIsSuccess
                    ? successLabel
                    : finalIsSaving
                      ? savingLabel
                      : label}
              </motion.span>
            </AnimatePresence>
          </div>

          {/* Action Area (Stable spot for the button + Close) */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
            <AnimatePresence mode="wait">
              {!finalIsSaving && !finalIsSuccess && !finalIsError ? (
                <motion.div
                  key="save-btn"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    sx={{
                      height: "32px",
                      borderRadius: "999px",
                      backgroundColor: brandColor,
                      color: "#ffffff",
                      textTransform: "none",
                      fontSize: "13px",
                      fontWeight: 700,
                      px: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "none",
                      transition: "all 0.2s ease",
                      "&:hover": { 
                        backgroundColor: brandColor, 
                        opacity: 0.9, 
                        boxShadow: "0 4px 12px -2px rgba(0,0,0,0.15)" 
                      },
                      "&:active": { transform: "scale(0.96)" }
                    }}
                  >
                    {saveLabel}
                  </Button>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {/* Close Button */}
            <button
              onClick={handleDismiss}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: "rgba(0,0,0,0.05)",
                color: darkBrandColor,
                cursor: "pointer",
                transition: "all 0.2s ease",
                padding: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.1)";
                e.currentTarget.style.transform = "rotate(90deg)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)";
                e.currentTarget.style.transform = "rotate(0deg)";
              }}
              aria-label="Dismiss"
            >
              <XIcon />
            </button>
          </div>
          
          <style>
            {`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default UnsavedChanges;
